import { ChangeEvent, FormEvent, forwardRef, useState } from "react";
import { AppButton, AppTextarea, Divider } from "@libs/zapi-ui/custom";
import { ArrowUpIcon, XIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useCommentStore } from "../model/store/commentsStore";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import { generateStubComment } from "../lib/generateStubComment";
import { useScopedTimeout } from "@/zapi-frontend/shared/hooks/useScopedTimeout";

type TCommentInputFieldProps = {
  isLoading: boolean;
  userId: number;
  firstName: string;
  lastName: string;
  onSubmit: () => void;
};

export const CommentInputField = forwardRef<HTMLTextAreaElement, TCommentInputFieldProps>((props, ref) => {
  const { isLoading = false, userId, firstName, lastName, onSubmit } = props;

  const { createComment } = useCommentStore();
  const { activeChapter } = useChapterStore();
  const { selectedProject } = useProjectStore();
  const { commentsUpdated } = useEditorStore();
  const { runTimeout } = useScopedTimeout();
  const [message, setMessage] = useState("");

  const projectId = selectedProject?.id;
  const chapterId = activeChapter?.id.toString() || "";

  function handleInputChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setMessage(e.target.value);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!userId || !message.trim() || !projectId || !chapterId) return;

    if (typeof ref === "object" && ref?.current) ref.current.style.height = "auto";
    setMessage("");

    runTimeout(() => {
      onSubmit();
    }, 0);
    await createComment({
      projectId,
      chapterId,
      content: message.trim(),
      stubComment: generateStubComment({ userId, firstName, lastName, content: message.trim() })
    });
    commentsUpdated?.();
  }

  function onInputHeightChange(e: FormEvent<HTMLTextAreaElement>) {
    const target = e.currentTarget as HTMLTextAreaElement;
    target.style.height = "auto";
    target.style.height = `${target.scrollHeight}px`;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn("flex flex-col h-auto relative p-1 rounded-b-[8px] bg-innoveraZinc-100")}
    >
      <div className="h-auto flex flex-col relative border border-innoveraZinc-150 rounded-[8px] bg-white transition-all ease duration-300 hover:bg-innoveraZinc-50 focus-within:shadow-md">
        <AppTextarea
          ref={ref}
          fullWidth
          className="min-h-[44px] max-h-[80px] resize-none !border-0 !ring-0 py-3 px-4"
          placeholder="Add a comment"
          value={message}
          onChange={handleInputChange}
          onInput={onInputHeightChange}
          disabled={isLoading}
          rows={1}
          data-testid="comment-input"
        />

        {!message && (
          <AppButton
            className="absolute bottom-1/2 translate-y-1/2 right-2"
            endAdornment={<ArrowUpIcon className="size-3" />}
            size="iconSm"
            type="submit"
            disabled
          />
        )}

        {!!message && (
          <>
            <Divider className="mb-3 mx-auto !w-[calc(100%-24px)] !bg-innoveraZinc-150" />
            <div className="flex gap-1 ml-auto mr-4 mb-3">
              <AppButton
                className="border-innoveraZinc-150"
                appearance="outlined"
                color="zinc100"
                endAdornment={<XIcon className="size-3 text-innoveraZinc-400" />}
                size="iconSm"
                type="button"
                onClick={() => setMessage("")}
              />
              <AppButton
                endAdornment={<ArrowUpIcon className="size-3" />}
                size="iconSm"
                type="submit"
                disabled={isLoading || !message.trim()}
                data-testid="post-comment-button"
              />
            </div>
          </>
        )}
      </div>
    </form>
  );
});
